import { useEffect, useMemo, useState } from 'react';

import { Empty, List, Typography } from 'antd';
import { useHistory, useParams } from 'react-router-dom';

import { ApiError, handleError } from '../../../api/base';
import UserService from '../../../api/user';
import { useAuth } from '../../../hooks/useAuth';
import { UserStreamSessionLogModel } from '../../../models/user-stream-session-log';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getUser } from '../../../store/features/users/usersSlice';
import { formatDate, UserPermissions } from '../../../util';
import Button from '../../elements/Button';
import NoPermission from '../../elements/NoPermission';
import ViewWrapper from '../../elements/ViewWrapper';
import './UserStreamSessionLogsView.less';

const { Text } = Typography;

const PAGE_SIZE = 20;

const getLogType = (log: UserStreamSessionLogModel): string => {
  const value =
    (typeof log.log_type === 'string' && log.log_type) ||
    (typeof log.type === 'string' && (log.type as string));
  return value || 'unknown';
};

const getLogContent = (log: UserStreamSessionLogModel): string => {
  const rawData = log.data;
  if (typeof rawData === 'string') {
    return rawData;
  }

  if (rawData !== undefined && rawData !== null) {
    try {
      return JSON.stringify(rawData, null, 2);
    } catch (_error) {
      return String(rawData);
    }
  }

  if (typeof log.message === 'string') {
    return log.message as string;
  }

  return '';
};

const getSizeBytes = (log: UserStreamSessionLogModel, content: string): number => {
  if (typeof log.size_bytes === 'number') {
    return log.size_bytes;
  }

  try {
    return new TextEncoder().encode(content).length;
  } catch (_error) {
    return content.length;
  }
};

const UserStreamSessionLogsView = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { id, sessionUuid } = useParams<{ id: string; sessionUuid: string }>();
  const { hasPermission } = useAuth();
  const { user, fetchingUser } = useAppSelector((state) => state.users);

  const [logs, setLogs] = useState<UserStreamSessionLogModel[]>([]);
  const [fetchingLogs, setFetchingLogs] = useState(false);
  const [selectedLog, setSelectedLog] = useState<UserStreamSessionLogModel | null>(
    null
  );

  const canViewSessionLogs = hasPermission(UserPermissions.UsersEdit);

  useEffect(() => {
    if (id) {
      dispatch(getUser(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    let isMounted = true;

    const fetchLogs = async () => {
      if (!id || !sessionUuid) {
        return;
      }

      setFetchingLogs(true);
      try {
        const resp = await UserService().listUserStreamSessionLogs(id, sessionUuid);
        if (isMounted) {
          const nextLogs = resp.data || [];
          setLogs(nextLogs);
          setSelectedLog(nextLogs[0] || null);
        }
      } catch (error) {
        handleError(error as ApiError);
      } finally {
        if (isMounted) {
          setFetchingLogs(false);
        }
      }
    };

    if (canViewSessionLogs) {
      fetchLogs();
    }

    return () => {
      isMounted = false;
    };
  }, [id, sessionUuid, canViewSessionLogs]);

  const selectedLogContent = useMemo(
    () => (selectedLog ? getLogContent(selectedLog) : ''),
    [selectedLog]
  );

  if (!canViewSessionLogs) {
    return <NoPermission />;
  }

  return (
    <ViewWrapper
      options={{
        goBack: true,
        loading: fetchingUser,
      }}
      headerTitle={
        user?.name
          ? `${user.name} Session Logs`
          : `Session Logs (${sessionUuid || 'unknown'})`
      }
    >
      <div className="session-logs-view">
        <div className="session-logs-list">
          <List
            loading={fetchingLogs}
            bordered
            dataSource={logs}
            locale={{
              emptyText: (
                <Empty
                  description="No session logs found."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            pagination={{
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
            }}
            renderItem={(log) => {
              const content = getLogContent(log);
              const sizeBytes = getSizeBytes(log, content);
              const createdAt = log.created_at
                ? formatDate(log.created_at, undefined, 'America/Chicago')
                : 'N/A';
              const selected = selectedLog === log;
              return (
                <List.Item
                  className={`session-log-item ${selected ? 'selected' : ''}`}
                  onClick={() => setSelectedLog(log)}
                >
                  <Text strong={selected}>{createdAt}</Text>
                  <Text type="secondary">{getLogType(log)}</Text>
                  <Text type="secondary">{sizeBytes} bytes</Text>
                </List.Item>
              );
            }}
          />
        </div>

        <div className="session-log-content-panel">
          <div className="session-log-content-header">
            <Text strong>Log content</Text>
          </div>
          <pre className="session-log-content">
            {selectedLogContent || 'Select a log entry to view its contents.'}
          </pre>
          <div className="session-log-actions">
            <Button onClick={() => history.push(`/users/${id}/stream-sessions`)}>
              Back to Stream Sessions
            </Button>
          </div>
        </div>
      </div>
    </ViewWrapper>
  );
};

export default UserStreamSessionLogsView;
